// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// tutorial.ts — seven scripted steps on a hand-cut board.
//
// THIS IS DATA, NOT A SECOND IMPLEMENTATION, AND THAT IS THE WHOLE POINT. Every step names a tool,
// a set of cells where that tool is allowed, and a predicate over real State that says when the
// step is done — all evaluated against the SHIPPED rules in sim.ts. A tutorial with its own private
// board logic is a lie the moment anybody touches the rules, and worse, it is not even a DETECTABLE
// lie: it keeps teaching the old game, confidently, forever. Because these steps are data,
// tests/tutorial.test.ts can replay `solve()` through the real `apply()` and assert each step is
// legal and reachable from the state the previous one produced.
//
// THE LAST STEP IS THE ONE THE WHOLE SEQUENCE EXISTS TO REACH. Steps 1-4 build a line and ship
// something, which is pleasant and teaches the verbs. Step 5 doubles the traffic onto one lane,
// step 6 makes the player WATCH that lane go orange, and step 7 asks them to fix it — and the fix
// is not an upgrade, because there are no upgrades. The fix is a second lane. That is the game.

import { Kind, KindT, modeOf } from './modes';
import { Action, H, Layout, N, SLOTS, State, W, idx } from './sim';

/** A fixed board: one shale seam at the top, the wharf at the bottom, and a clear column between. */
export function tutorialBoard(): Layout {
  const layout: Layout = { kind: new Uint8Array(N), dir: new Uint8Array(N), patch: new Uint8Array(N) };
  for (const [x, y] of [
    [5, 2],
    [6, 2],
    [5, 3],
    [6, 3],
  ]) layout.patch[idx(x, y)] = 1;
  for (const [x, y] of [
    [2, 5],
    [2, 6],
  ]) layout.patch[idx(x, y)] = 2;
  for (let dy = 0; dy < 2; dy++) {
    for (let dx = 0; dx < 2; dx++) layout.kind[idx(5 + dx, H - 2 + dy)] = Kind.Wharf;
  }
  layout.kind[idx(5, 3)] = Kind.Digger;
  return layout;
}

const C = (x: number, y: number): number => idx(x, y);
export const LANE = [C(5, 4), C(5, 5), C(5, 6)];
export const SMELTER_AT = C(5, 7);
export const LANE2 = [C(5, 8), C(5, 9)];
export const DIGGER2 = C(6, 3);
export const JOIN = C(6, 4);
export const SMELTER2 = C(6, 7);
export const OWN_LANE = [C(6, 5), C(6, 6), C(6, 8), C(6, 9)];

export interface TutorialStep {
  /** One sentence, imperative, naming the thing to touch. */
  copy: string;
  /** The tool that must be armed. Undefined means this step is a watch step. */
  tool?: KindT;
  /** Erase is armed instead of a tool. */
  erase?: boolean;
  /** Cells where the action is permitted. Everything else is refused. */
  cells: number[];
  /** True once the player has done the thing. Evaluated against real State. */
  done: (s: State) => boolean;
  /** A watch step lets the clock run; a build step holds it. */
  watch?: boolean;
  /** The canonical solution, replayed by tests/tutorial.test.ts through the shipped rules. */
  solve: (s: State) => Action[];
}

const allBelts = (s: State, cells: number[]): boolean => cells.every((c) => s.kind[c] === Kind.Belt);

/** How many items are resident on a carrier cell right now. */
const itemsOn = (s: State, c: number): number => {
  let n = 0;
  for (let sl = 0; sl < SLOTS; sl++) if (s.slot[c * SLOTS + sl]) n++;
  return n;
};

export const STEPS: TutorialStep[] = [
  {
    copy: 'Tap the three empty cells below the digger to lay a belt.',
    tool: Kind.Belt,
    cells: LANE,
    done: (s) => allBelts(s, LANE),
    solve: () => LANE.map((c) => ({ a: 'place', kind: Kind.Belt, cell: c, dir: 2 }) as Action),
  },
  {
    copy: 'Put a smelter at the end of the belt.',
    tool: Kind.Smelter,
    cells: [SMELTER_AT],
    done: (s) => s.kind[SMELTER_AT] === Kind.Smelter,
    solve: () => [{ a: 'place', kind: Kind.Smelter, cell: SMELTER_AT, dir: 0 }],
  },
  {
    copy: 'Carry on down to the wharf.',
    tool: Kind.Belt,
    cells: LANE2,
    done: (s) => allBelts(s, LANE2),
    solve: () => LANE2.map((c) => ({ a: 'place', kind: Kind.Belt, cell: c, dir: 2 }) as Action),
  },
  {
    copy: 'Two shale make one billet. Watch one reach the wharf.',
    cells: [],
    watch: true,
    // A billet is worth one scrip and is not an armature, so `shipped` stays 0 here — the honest
    // signal that something reached the wharf is the money.
    done: (s) => s.scrip > modeOf(s.mode).startScrip,
    solve: () => [],
  },
  {
    copy: 'Add a second digger on the seam, and a belt joining it to the lane.',
    tool: Kind.Digger,
    cells: [DIGGER2, JOIN],
    done: (s) => s.kind[DIGGER2] === Kind.Digger && s.kind[JOIN] === Kind.Belt,
    solve: () => [
      { a: 'place', kind: Kind.Digger, cell: DIGGER2, dir: 0 },
      { a: 'place', kind: Kind.Belt, cell: JOIN, dir: 3 },
    ],
  },
  {
    // THIS STEP TAUGHT THE WRONG LESSON UNTIL tests/tutorial.test.ts REFUSED IT. It used to say the
    // shared lane now carried two a second and asked the player to watch the ground rut. It cannot:
    // one smelter consumes 0.80 shale/s, so the lane's THROUGHPUT is 0.80/s no matter how many
    // diggers feed it — well under the 1.25/s a cell carries for free. The extra shale does not
    // cross the ground, it stands on it, and standing traffic does not rut. Measured: the lane
    // reached rut 5 in ninety seconds of sim, so the step dead-ended forever.
    //
    // The honest lesson at this board size is the one that is actually happening — the consumer is
    // the bottleneck and the lane is backing up — and it is the same skill: read the lane, find the
    // real constraint, route around it. Rut is what the same reading finds once a real run has
    // three lines sharing a corridor, which is where the numbers genuinely exceed 1.25/s.
    copy: 'One smelter eats two shale every two and a half seconds. Watch the lane back up.',
    cells: [],
    watch: true,
    done: (s) => LANE.some((c) => itemsOn(s, c) >= 2),
    solve: () => [],
  },
  {
    copy: 'You cannot upgrade a belt. Take up the join and give that digger its own lane and smelter.',
    erase: true,
    cells: [JOIN, ...OWN_LANE, SMELTER2],
    done: (s) => s.dir[JOIN] === 2 && s.kind[SMELTER2] === Kind.Smelter && allBelts(s, OWN_LANE),
    // Lay the new lane FIRST, then re-lay the join. A tapped belt points at whichever neighbour can
    // take an item and is nearest the wharf, so the join only turns south once there is something
    // south of it to turn toward — doing it the other way round re-points it back at the old lane.
    solve: () => [
      ...OWN_LANE.map((c) => ({ a: 'place', kind: Kind.Belt, cell: c, dir: 2 }) as Action),
      { a: 'place', kind: Kind.Smelter, cell: SMELTER2, dir: 0 },
      { a: 'remove', cell: JOIN },
      { a: 'place', kind: Kind.Belt, cell: JOIN, dir: 2 },
    ],
  },
];

/**
 * What the step permits. Lives here rather than in main.ts so the test can check REFUSAL as well as
 * success — "a wrong move is refused without breaking the sequence" is half of what makes a
 * tutorial a tutorial, and it is not observable from the happy path.
 */
export function allowedIn(stepIndex: number, cell: number, kind: KindT | null): boolean {
  const st = STEPS[stepIndex];
  if (!st || st.watch) return false;
  if (!st.cells.includes(cell)) return false;
  if (st.erase) return true;
  return kind === st.tool || kind === Kind.Belt;
}

/** The board is 12 wide; every scripted cell must be on it. Cheap, and it has caught a typo. */
export function stepsAreOnTheBoard(): boolean {
  return STEPS.every((st) => st.cells.every((c) => c >= 0 && c < W * H));
}
